import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { membershipCopy, type MembershipState } from "@/lib/membership";
import { GESTAO_URL } from "@/lib/sensitive-field-errors";
import { Logo } from "./logo";
import { Button } from "./ui/button";
import { SiteFooter } from "./site-footer";
import { AlertTriangle, Clock, ExternalLink, LifeBuoy, LogOut, UserX } from "lucide-react";

type Props = {
  state: Exclude<MembershipState, "approved">;
};

/**
 * Tela exibida ao usuário autenticado que NÃO está autorizado a entrar na área
 * de membros: cadastro em análise, reprovado ou inexistente.
 */
export function MembershipBlockedScreen({ state }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const copy = membershipCopy(state);

  const signOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  const Icon = state === "pending" ? Clock : state === "rejected" ? AlertTriangle : UserX;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border/60 bg-background/95">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 md:px-6">
          <Logo size={36} />
          <Button variant="ghost" size="sm" onClick={signOut}>
            <LogOut className="mr-2 h-4 w-4" /> Sair
          </Button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 py-12">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold/15">
          <Icon className="h-7 w-7 text-gold-foreground" />
        </div>
        <h1 className="mt-5 text-center font-serif text-2xl font-semibold text-primary sm:text-3xl">
          {copy.title}
        </h1>
        <p className="mt-3 text-center text-sm leading-relaxed text-muted-foreground sm:text-base">
          {copy.description}
        </p>

        <div className="mt-8 flex flex-col gap-2 sm:flex-row">
          <Button asChild variant="outline" className="flex-1">
            <a href={GESTAO_URL} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" /> Gestão de cadastros
            </a>
          </Button>
          <Button asChild className="flex-1">
            <Link to="/suporte">
              <LifeBuoy className="mr-2 h-4 w-4" /> Suporte & FAQ
            </Link>
          </Button>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
